/**
 * Самописный History-роутер kikayoo (И6, phase-2-plan §2.6/§И6).
 *
 * Принципы:
 * - 0 новых npm-зависимостей (без react-router): тонкая обёртка над `window.history`
 *   и событием `popstate`.
 * - Единое внешнее «хранилище pathname» + Set слушателей. React подключается через
 *   `useSyncExternalStore` (хук `useRoute`) — никаких локальных подписок в компонентах.
 * - Программная навигация: `navigate(path)` → pushState + notify (новая запись в
 *   истории); `redirect(path)` → replaceState + notify (для редиректов неизвестных
 *   маршрутов, без засорения истории кнопкой «назад»).
 *
 * Маршрутизируются только «закладляемые» страницы: `/`, `/about`, `/games`,
 * `/edit`, `/me` (личный кабинет, И4), `/me/history` (полная история сессий хоста,
 * оконная пагинация — Фаза 12 B4; номер страницы — query `?page=`, читается в App.tsx
 * по образцу `?pin=`/`?token=`), `/admin` (операционный дашборд, Фаза 7,
 * только `role==='admin'`), `/login`, `/register`, `/forgot`,
 * `/reset`, `/verify`, `/privacy`. Внутренние потоки ведущего/игрока остаются
 * state-машиной (PIN в URL не нужен); QR-вход сохранён через `?pin=` (читается в
 * App.tsx, не здесь); токен восстановления пароля — через `?token=` (читается в
 * App.tsx для `/reset`).
 *
 * Публичные открытые квизы (Фаза 10, И11) — параметризованный путь `/q/<slug>`:
 * строгий union Route статических литералов не расширяется (не ломаем контракт);
 * вместо этого prefix-match `matchOpenRoute` (slug — hex), который `isKnownRoute`
 * учитывает как известный маршрут (иначе `/q/…` редиректился бы на `/`).
 */
import { useSyncExternalStore } from 'react';

/** Литералы известных маршрутов (тип-безопасная навигация и диспетчеризация). */
export type Route =
  | '/'
  | '/about'
  | '/games'
  | '/edit'
  | '/me'
  | '/me/history'
  | '/admin'
  | '/login'
  | '/register'
  | '/forgot'
  | '/reset'
  | '/verify'
  | '/privacy';

type Listener = () => void;

const listeners = new Set<Listener>();
const KNOWN_ROUTES: ReadonlySet<string> = new Set<Route>([
  '/',
  '/about',
  '/games',
  '/edit',
  '/me',
  '/me/history',
  '/admin',
  '/login',
  '/register',
  '/forgot',
  '/reset',
  '/verify',
  '/privacy',
]);

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): string {
  return window.location.pathname;
}

/** SSR/тестовый снапшот: нет `window` → корень. В проде SPA `window` всегда есть. */
function getServerSnapshot(): string {
  return typeof window === 'undefined' ? '/' : window.location.pathname;
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', notify);
}

/** Программная навигация: `history.pushState` + уведомление подписчиков. */
export function navigate(path: string): void {
  if (typeof window === 'undefined' || window.location.pathname === path) return;
  window.history.pushState(null, '', path);
  notify();
}

/** Замена URL без новой записи в истории (редирект неизвестного маршрута на `/`). */
export function redirect(path: string): void {
  if (typeof window === 'undefined' || window.location.pathname === path) return;
  window.history.replaceState(null, '', path);
  notify();
}

/** Префикс публичных открытых квизов: `/q/<slug>` (slug — 32 hex, Фаза 10 И11). */
export const OPEN_ROUTE_PREFIX = '/q/';

/**
 * Slug открытого квиза из pathname: `/q/<hex 8..64>` → slug; иначе null.
 * Диапазон длин — защитный (сервер генерирует 32 hex, префикс-матч не должен
 * ловить мусор вроде `/q/` или `/q/x!y`).
 */
export function matchOpenRoute(pathname: string): string | null {
  if (!pathname.startsWith(OPEN_ROUTE_PREFIX)) return null;
  const slug = pathname.slice(OPEN_ROUTE_PREFIX.length);
  return /^[0-9a-f]{8,64}$/i.test(slug) ? slug : null;
}

/** Принадлежит ли pathname известному маршруту (type guard для диспетчера App). */
export function isKnownRoute(pathname: string): pathname is Route {
  return KNOWN_ROUTES.has(pathname) || matchOpenRoute(pathname) !== null;
}

/** Текущий pathname с реактивной подпиской на pushState/popstate. */
export function useRoute(): string {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
